import { useMemo } from "react";
import { bool, number } from "prop-types";
import { observer } from "mobx-react";
import { isSafari } from "react-device-detect";
import { BreakdownColumns } from "@repo/utils";
import {
  ValueCell,
  useEstimateEditorSettings,
  useEstimateStructure,
  useEstimateTableSettings,
  useEstimateEditorStore
} from "@tools";
import { CELL_WIDTHS } from "@utils";
import { Grid } from "@material-ui/core";
import classnames from "classnames";
import useCellStyle from "@tools/EstimateEditor/styles/CommonTableCellStyles.style";

export const PlaceholderCell = observer(({
  fullWidth,
  height,
  tableDepth,
}) => {
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { allowEdition, showPdfCover } = useEstimateEditorSettings();
  const {
    emptyWorkTypeCells,
    displayColumns,
    showCombinedHours,
    materialsSection,
    visibleWorkTypes,
    workTypeColumnsMaxState,
    totalWorkloadColumnMaxState,
    unpinnedColumnsWidth,
  } = useEstimateTableSettings();
  const cellClasses = useCellStyle();
  
  const { showBreakdownColumns } = structure.settings;
  
  const priceCellIndex = useMemo(() => (
    displayColumns.findIndex(c => c.name === BreakdownColumns.PRICE)
  ), [displayColumns]);
  
  const priceCellSettings = useMemo(() => (
    displayColumns[priceCellIndex]
  ), [displayColumns, priceCellIndex]);
  
  const priceIsSticky = useMemo(() => (
    priceCellSettings?.applyPin && (!isSafari || !showPdfCover)
  ), [tableDepth, priceCellSettings?.applyPin]);
  
  const width = useMemo(() => {
    if(fullWidth || !showBreakdownColumns)
      return undefined;
      
    let a = unpinnedColumnsWidth || 0;
    const modifier = (allowEdition || visibilityMode ? 16 : 0);
    
    if(materialsSection) {
      if(!showCombinedHours)
        a += 2 * CELL_WIDTHS.VALUE.SMALL
    } else if (showCombinedHours)
      a += (totalWorkloadColumnMaxState) ? CELL_WIDTHS.VALUE.LARGE : CELL_WIDTHS.VALUE.MEDIUM;
    else
      visibleWorkTypes.map((workType) => (
        a += (workTypeColumnsMaxState[workType.id] ? CELL_WIDTHS.VALUE.MEDIUM : CELL_WIDTHS.VALUE.SMALL) + modifier
      ))
    
    if( !showCombinedHours && emptyWorkTypeCells > 0)
      a += emptyWorkTypeCells * CELL_WIDTHS.VALUE.SMALL;
    return a;
  }, [ allowEdition, visibilityMode, fullWidth, showBreakdownColumns, showCombinedHours, visibleWorkTypes, workTypeColumnsMaxState, totalWorkloadColumnMaxState, materialsSection, emptyWorkTypeCells, unpinnedColumnsWidth ]);
  
  return (
    <>
      <Grid
        item container
        className={classnames(
          "cell",
          cellClasses.cell,
          cellClasses.cellInner,
          fullWidth && "w-full",
          "pr-4",
        )}
        style={{
          height,
          minWidth: width,
          maxWidth: width,
        }}
      />
      {
        priceCellSettings?.isVisible &&
        <ValueCell
          useDynamicWidth
          colName={ BreakdownColumns.PRICE }
          isSticky={ priceIsSticky }
          allowOverflowDisplay={ priceIsSticky }
          extendPadding={priceCellIndex === displayColumns.length - 1 }
          textPresetClass="preset-price"
        />
      }
    </>
  )
});

PlaceholderCell.propTypes = {
  fullWidth: bool,
  height: number,
  tableDepth: number,
}